import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Plan-scoped financing applications (used by FinancingPanel.tsx) ──────────

export type FinancingProvider = 'alphaeon' | 'carecredit' | 'lending_usa' | 'other';
export type FinancingApplicationStatus = 'pending' | 'submitted' | 'approved' | 'denied' | 'cancelled';

export interface FinancingApplication {
  applicationId: number;
  accountId: number;
  patientId: number;
  planId: number | null;
  provider: FinancingProvider;
  applicationStatus: FinancingApplicationStatus;
  requestedAmount: number | null;
  approvedAmount: number | null;
  confirmationNumber: string | null;
  notes: string | null;
  submittedAt: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFinancingApplicationPayload {
  patientId: number;
  planId: number;
  provider: FinancingProvider;
  requestedAmount: number;
  notes?: string;
}

export interface UpdateFinancingApplicationPayload {
  applicationStatus?: FinancingApplicationStatus;
  approvedAmount?: number;
  confirmationNumber?: string;
  decidedAt?: string;
  notes?: string;
}

export function listFinancingApplications(params: {
  patientId?: number;
  planId?: number;
}): Promise<FinancingApplication[]> {
  return apiClient
    .get<ApiResponse<FinancingApplication[]>>('/financing', { params })
    .then((r) => r.data.data);
}

export function createFinancingApplication(
  data: CreateFinancingApplicationPayload,
): Promise<FinancingApplication> {
  return apiClient
    .post<ApiResponse<FinancingApplication>>('/financing', data)
    .then((r) => r.data.data);
}

export function updateFinancingApplication(
  id: number,
  data: UpdateFinancingApplicationPayload,
): Promise<FinancingApplication> {
  return apiClient
    .patch<ApiResponse<FinancingApplication>>(`/financing/${id}`, data)
    .then((r) => r.data.data);
}

// ─── Patient-scoped financing records (used by FinancingTab.tsx) ─────────────

export interface FinancingRecord {
  patientsFinancingMatchID: number;
  accountID: number;
  patientID: number;
  patientsFinancingID: number;
  response: string;
  added: string;
  active: string;
  provider: string | null;
}

export function getPatientFinancing(patientId: number): Promise<FinancingRecord[]> {
  return apiClient
    .get<ApiResponse<FinancingRecord[]>>('/financing', { params: { patientId } })
    .then((r) => r.data.data);
}

export function createFinancingRecord(data: {
  patientId: number;
  response: string;
}): Promise<{ applicationId: number }> {
  return apiClient
    .post<ApiResponse<{ applicationId: number }>>('/financing', data)
    .then((r) => r.data.data);
}

export function updateFinancingRecord(
  id: number,
  data: Record<string, unknown>,
): Promise<{ success: boolean }> {
  return apiClient
    .patch<ApiResponse<{ success: boolean }>>(`/financing/${id}`, data)
    .then((r) => r.data.data);
}
